// Global debug flag
window.MENU_DRAWER_DEBUG = false;

// Add debug styles
const debugStyle = document.createElement('style');
debugStyle.textContent = `
  .menu-drawer-debug {
    position: fixed;
    top: 10px;
    right: 10px;
    background: rgba(0,0,0,0.8);
    color: white;
    padding: 10px;
    font-family: monospace;
    z-index: 9999;
    max-width: 300px;
    overflow: auto;
    max-height: 200px;
  }
`;
document.head.appendChild(debugStyle);

// Create debug output element
const debugOutput = document.createElement('div');
debugOutput.className = 'menu-drawer-debug';
debugOutput.style.display = 'none';
document.body.appendChild(debugOutput);

// Debug logging function
function debugLog(message, data = null) {
  if (!window.MENU_DRAWER_DEBUG) return;
  
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  const logMessage = `[${timestamp}] ${message}`;
  
  // Console output
  console.log(logMessage);
  if (data) console.log(data);
  
  // Visual output
  debugOutput.style.display = 'block';
  debugOutput.innerHTML = `${logMessage}<br>${debugOutput.innerHTML}`;
  
  // Trim visual output
  if (debugOutput.innerHTML.split('<br>').length > 10) {
    debugOutput.innerHTML = debugOutput.innerHTML.split('<br>').slice(0, 10).join('<br>');
  }
}

debugLog('🔥 Menu Drawer A11y Script Loaded');

class MenuDrawerA11y extends HTMLElement {
  constructor() {
    super();
    debugLog('🏗️ MenuDrawerA11y: Initializing...');
    
    this.detailsElement = this.querySelector('details');
    this.menuDrawer = this.querySelector('#menu-drawer');
    this.summaryButton = this.querySelector('summary');
    this.closeButton = this.querySelector('.menu-drawer__close-button');
    this.focusableElements = null;
    this.isOpen = false;
    this.debugId = Math.random().toString(36).substr(2, 9);
    
    debugLog('🔍 MenuDrawerA11y: Found elements', {
      detailsElement: !!this.detailsElement,
      menuDrawer: !!this.menuDrawer,
      summaryButton: !!this.summaryButton,
      closeButton: !!this.closeButton,
      instanceId: this.debugId
    });
    
    // Bind methods
    this.onDetailsToggle = this.onDetailsToggle.bind(this);
    this.trapFocus = this.trapFocus.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    
    // Create mutation observer to watch for changes
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'open') {
          this.isOpen = this.detailsElement.hasAttribute('open');
          debugLog(`🔄 MenuDrawerA11y [${this.debugId}]: Modal ${this.isOpen ? 'opened' : 'closed'}`);
          if (this.isOpen) {
            this.setupFocusTrap();
          } else {
            this.teardownFocusTrap();
          }
        }
      });
    });
    
    this.init();
    debugLog(`✅ MenuDrawerA11y [${this.debugId}]: Initialization complete`);
  }

  setupFocusTrap() {
    document.addEventListener('focusin', this.handleFocusIn);
    this.setAriaHiddenOnOtherElements(true);
    requestAnimationFrame(() => {
      const modalTitle = document.getElementById('menu-drawer-title');
      if (modalTitle) {
        modalTitle.focus();
      }
    });
  }

  teardownFocusTrap() {
    document.removeEventListener('focusin', this.handleFocusIn);
    this.setAriaHiddenOnOtherElements(false);
  }

  handleFocusIn(event) {
    if (this.isOpen && !this.menuDrawer.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();
      const focusableElements = this.getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }

  init() {
    // Set initial ARIA states
    this.summaryButton.setAttribute('aria-expanded', 'false');
    
    // Start observing the details element for changes
    this.observer.observe(this.detailsElement, { attributes: true });
    
    // Add event listeners
    this.detailsElement.addEventListener('toggle', this.onDetailsToggle);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    // Clean up event listeners
    this.detailsElement?.removeEventListener('toggle', this.onDetailsToggle);
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('focusin', this.handleFocusIn);
    
    // Disconnect observer
    this.observer.disconnect();
    
    // Clean up any remaining aria-hidden attributes
    this.setAriaHiddenOnOtherElements(false);
  }

  onDetailsToggle(event) {
    const isOpen = this.detailsElement.hasAttribute('open');
    this.summaryButton.setAttribute('aria-expanded', isOpen.toString());
    this.isOpen = isOpen;

    if (!isOpen) {
      // Return focus to menu button when closing
      this.summaryButton.focus();
    }
  }

  handleKeydown(event) {
    const isModalOpen = this.detailsElement.hasAttribute('open');
    
    if (!isModalOpen) {
      console.log(`🚫 MenuDrawerA11y [${this.debugId}]: Ignoring keydown - modal is closed`);
      return;
    }

    const activeElement = document.activeElement;
    console.log(`🎯 MenuDrawerA11y [${this.debugId}]: Keydown`, {
      key: event.key,
      target: {
        tag: event.target?.tagName,
        id: event.target?.id,
        class: event.target?.className,
        text: event.target?.textContent?.trim().substring(0, 30)
      },
      activeElement: {
        tag: activeElement?.tagName,
        id: activeElement?.id,
        class: activeElement?.className,
        text: activeElement?.textContent?.trim().substring(0, 30),
        isInModal: this.menuDrawer?.contains(activeElement)
      }
    });

    switch (event.key) {
      case 'Escape':
        console.log(`🚪 MenuDrawerA11y [${this.debugId}]: Escape pressed - closing modal`);
        event.preventDefault();
        this.closeMenuDrawer();
        break;
      case 'Tab':
        console.log(`⌨️ MenuDrawerA11y [${this.debugId}]: Tab pressed - trapping focus`);
        event.preventDefault(); // Always prevent default tab behavior
        this.trapFocus(event);
        break;
    }
  }

  trapFocus(event) {
    if (!this.isOpen) {
      console.log(`🚫 MenuDrawerA11y [${this.debugId}]: Trap focus ignored - modal is closed`);
      return;
    }

    const focusableElements = this.getFocusableElements();
    debugLog(`📋 MenuDrawerA11y [${this.debugId}]: Found focusable elements:`, 
      focusableElements.map((el, i) => ({
        index: i,
        tag: el.tagName,
        id: el.id,
        class: el.className,
        text: el.textContent?.trim().substring(0, 30)
      }))
    );

    if (!focusableElements.length) {
      debugLog(`⚠️ MenuDrawerA11y [${this.debugId}]: No focusable elements found in modal`);
      return;
    }

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const currentElement = document.activeElement;

    // Always prevent the default tab behavior
    event.preventDefault();

    debugLog(`🔄 MenuDrawerA11y [${this.debugId}]: Focus trap state:`, {
      currentElement: {
        tag: currentElement?.tagName,
        id: currentElement?.id,
        class: currentElement?.className,
        isInModal: this.menuDrawer.contains(currentElement)
      },
      firstFocusable: {
        tag: firstFocusable?.tagName,
        id: firstFocusable?.id,
        text: firstFocusable?.textContent?.trim().substring(0, 30)
      },
      lastFocusable: {
        tag: lastFocusable?.tagName,
        id: lastFocusable?.id,
        text: lastFocusable?.textContent?.trim().substring(0, 30)
      },
      direction: event.shiftKey ? 'backward' : 'forward'
    });

    // If focus somehow got outside the modal, force it back
    if (!this.menuDrawer.contains(currentElement)) {
      debugLog(`⚠️ MenuDrawerA11y [${this.debugId}]: Focus escaped modal - forcing back to first element`);
      firstFocusable.focus();
      return;
    }

    // Find the current element's index in the focusable elements
    const currentIndex = focusableElements.indexOf(currentElement);
    debugLog(`📍 MenuDrawerA11y [${this.debugId}]: Current focus index: ${currentIndex}`);
    
    if (event.shiftKey) {
      // Going backward
      if (currentIndex <= 0) {
        debugLog(`↩️ MenuDrawerA11y [${this.debugId}]: Wrapping to last element`);
        lastFocusable.focus();
      } else {
        debugLog(`⬅️ MenuDrawerA11y [${this.debugId}]: Moving to previous element`);
        focusableElements[currentIndex - 1].focus();
      }
    } else {
      // Going forward
      if (currentIndex >= focusableElements.length - 1) {
        debugLog(`↪️ MenuDrawerA11y [${this.debugId}]: Wrapping to first element`);
        firstFocusable.focus();
      } else {
        debugLog(`➡️ MenuDrawerA11y [${this.debugId}]: Moving to next element`);
        focusableElements[currentIndex + 1].focus();
      }
    }
  }

  handleOverlayClick(event) {
    if (event.target.classList.contains('menu-drawer-container')) {
      this.closeMenuDrawer();
    }
  }

  closeMenuDrawer() {
    this.detailsElement.removeAttribute('open');
  }

  getFocusableElements() {
    // Get all potentially focusable elements
    const selector = `
      a[href]:not([tabindex="-1"]):not([disabled]),
      button:not([tabindex="-1"]):not([disabled]),
      textarea:not([tabindex="-1"]):not([disabled]),
      input[type="text"]:not([tabindex="-1"]):not([disabled]),
      input[type="radio"]:not([tabindex="-1"]):not([disabled]),
      input[type="checkbox"]:not([tabindex="-1"]):not([disabled]),
      select:not([tabindex="-1"]):not([disabled]),
      [tabindex]:not([tabindex="-1"]):not([disabled]),
      [role="button"]:not([tabindex="-1"]):not([disabled])
    `;

    // Get all elements that match our selector within the modal
    const elements = Array.from(this.menuDrawer.querySelectorAll(selector));

    // Filter out hidden elements and those with display: none or visibility: hidden
    const visibleElements = elements.filter(el => {
      if (!el.offsetParent) return false; // Element or ancestor is hidden
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && 
             style.visibility !== 'hidden' && 
             style.opacity !== '0' &&
             el.offsetWidth > 0 && 
             el.offsetHeight > 0;
    });

    // Add the modal title if it exists (it has tabindex="-1" but we want it focusable)
    const modalTitle = document.getElementById('menu-drawer-title');
    if (modalTitle) {
      visibleElements.unshift(modalTitle);
    }

    // Log what we found
    console.log('Focusable elements:', visibleElements.map(el => ({
      tag: el.tagName,
      id: el.id,
      class: el.className,
      text: el.textContent?.trim().substring(0, 30)
    })));

    return visibleElements;
  }

  setAriaHiddenOnOtherElements(hide) {
    if (hide) {
      // Make everything outside the modal inert
      document.querySelectorAll('body > *').forEach(element => {
        if (!this.contains(element)) {
          element.setAttribute('inert', '');
          element.setAttribute('aria-hidden', 'true');
        }
      });

      // Ensure our modal container is not inert
      this.removeAttribute('inert');
      this.removeAttribute('aria-hidden');
      this.menuDrawer.removeAttribute('inert');
      this.menuDrawer.removeAttribute('aria-hidden');
    } else {
      // Remove inert from all elements
      document.querySelectorAll('[inert]').forEach(element => {
        element.removeAttribute('inert');
        element.removeAttribute('aria-hidden');
      });
    }
  }
}

// Only define the custom element if it hasn't been defined yet
if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', MenuDrawerA11y);
}

// Fallback A11Y enhancer for themes that already define <header-drawer>
// Attaches a lightweight focus trap without redefining the element
(function () {
  function selectFocusable(container) {
    const selector = [
      'a[href]:not([tabindex="-1"]):not([disabled])',
      'button:not([tabindex="-1"]):not([disabled])',
      'textarea:not([tabindex="-1"]):not([disabled])',
      'input:not([tabindex="-1"]):not([disabled])',
      'select:not([tabindex="-1"]):not([disabled])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])',
      '[role="button"]:not([tabindex="-1"]):not([disabled])'
    ].join(',');

    return Array.from(container.querySelectorAll(selector)).filter((el) => {
      if (!el.offsetParent) return false;
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetWidth > 0 && el.offsetHeight > 0;
    });
  }

  function attachEnhancer(root) {
    if (!root || root.__waayA11yAttached) return;
    root.__waayA11yAttached = true;

    const details = root.querySelector('details');
    const drawer = root.querySelector('#menu-drawer');
    const summary = root.querySelector('summary');
    const title = document.getElementById('menu-drawer-title');

    if (!details || !drawer) return;

    function onKeydown(e) {
      if (!details.hasAttribute('open')) return;
      if (e.key !== 'Tab') return;

      const elements = selectFocusable(drawer);
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function setAriaHiddenOutside(state) {
      // Hide all body children that do not contain the drawer (skip script/style)
      Array.from(document.body.children).forEach((el) => {
        if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE') return;
        if (!el.contains(drawer)) {
          if (state) {
            if (!el.hasAttribute('aria-hidden')) el.setAttribute('data-prev-aria-hidden', 'none');
            el.setAttribute('aria-hidden', 'true');
            if (!el.hasAttribute('inert')) el.setAttribute('data-prev-inert', 'none');
            el.setAttribute('inert', '');
          } else {
            if (el.getAttribute('data-prev-aria-hidden') === 'none') {
              el.removeAttribute('aria-hidden');
              el.removeAttribute('data-prev-aria-hidden');
            }
            if (el.getAttribute('data-prev-inert') === 'none') {
              el.removeAttribute('inert');
              el.removeAttribute('data-prev-inert');
            }
          }
        }
      });

      // Within the ancestor that contains the drawer (e.g., header), hide its direct children except the root
      const ancestor = root.parentElement; // header element
      if (ancestor && ancestor.contains(drawer)) {
        Array.from(ancestor.children).forEach((child) => {
          if (!child.contains(drawer)) {
            if (state) {
              if (!child.hasAttribute('aria-hidden')) child.setAttribute('data-prev-aria-hidden', 'none');
              child.setAttribute('aria-hidden', 'true');
              if (!child.hasAttribute('inert')) child.setAttribute('data-prev-inert', 'none');
              child.setAttribute('inert', '');
            } else {
              if (child.getAttribute('data-prev-aria-hidden') === 'none') {
                child.removeAttribute('aria-hidden');
                child.removeAttribute('data-prev-aria-hidden');
              }
              if (child.getAttribute('data-prev-inert') === 'none') {
                child.removeAttribute('inert');
                child.removeAttribute('data-prev-inert');
              }
            }
          }
        });
      }
    }

    function setSiblingSummaryHidden(state) {
      if (!summary) return;
      if (state) {
        if (!summary.hasAttribute('aria-hidden')) summary.setAttribute('data-prev-aria-hidden', 'none');
        summary.setAttribute('aria-hidden', 'true');
        if (!summary.hasAttribute('tabindex')) summary.setAttribute('data-prev-tabindex', 'none');
        summary.setAttribute('tabindex', '-1');
      } else {
        if (summary.getAttribute('data-prev-aria-hidden') === 'none') {
          summary.removeAttribute('aria-hidden');
          summary.removeAttribute('data-prev-aria-hidden');
        }
        if (summary.getAttribute('data-prev-tabindex') === 'none') {
          summary.removeAttribute('tabindex');
          summary.removeAttribute('data-prev-tabindex');
        }
      }
    }

    function onToggle() {
      const open = details.hasAttribute('open');
      if (open) {
        setTimeout(() => {
          if (title) {
            title.focus();
          } else if (drawer) {
            drawer.setAttribute('tabindex', '-1');
            drawer.focus();
          }
        }, 50);
        document.addEventListener('keydown', onKeydown);
        // Hide outside content and the toggle summary from screen readers while open
        setAriaHiddenOutside(true);
        setSiblingSummaryHidden(true);
      } else {
        document.removeEventListener('keydown', onKeydown);
        if (summary) summary.focus();
        // Restore aria-hidden when closed
        setAriaHiddenOutside(false);
        setSiblingSummaryHidden(false);
      }
    }

    details.addEventListener('toggle', onToggle);
  }

  if (window.customElements && window.customElements.whenDefined) {
    window.customElements.whenDefined('header-drawer').then(() => {
      document.querySelectorAll('header-drawer').forEach(attachEnhancer);
    });
  } else {
    document.querySelectorAll('header-drawer').forEach(attachEnhancer);
  }
})();
